'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PaginationState, SortState, UseDataTableConfig, UseDataTableReturn } from './types';

export function useDataTable<T>(config: UseDataTableConfig<T>): UseDataTableReturn<T> {
  const { data, pageSize = 10, defaultSort, sortFn } = config;

  const [sort, setSort] = useState<SortState>(defaultSort ?? { key: '', direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    if (sortFn) return sortFn(data, sort);
    return data;
  }, [data, sort, sortFn]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const safePage = Math.min(page, totalPages);

  const processedData = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [data.length]);

  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(Math.max(1, newPage));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const pagination: PaginationState = useMemo(
    () => ({
      page: safePage,
      totalPages,
      totalItems: data.length,
    }),
    [safePage, totalPages, data.length],
  );

  return {
    sort,
    onSortChange: handleSortChange,
    selectedIds,
    onSelectionChange: setSelectedIds,
    clearSelection,
    pagination,
    onPageChange: handlePageChange,
    processedData,
  };
}
